import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaSearch, FaCheck } from 'react-icons/fa';
import { notify } from '../CustomToast';
import userService from '../../services/userService';
import teamService from '../../services/teamService';
import videoCallService from '../../services/videoCallService';
import './AddMembersModal.css';

const AVATAR_COLOR_COUNT = 8;

function getAvatarColorIndex(name) {
    if (!name) return 0;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % AVATAR_COLOR_COUNT;
}

const AddMembersModal = ({ meeting, existingParticipantIds, onClose, onAdded }) => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const meetingId = meeting?.id;
    const teamId = meeting?.team_id;

    useEffect(() => {
        let cancelled = false;

        let currentUser = {};
        try {
            currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            currentUser = {};
        }
        const currentUserId = currentUser?.id;

        const existingSet = new Set(
            (existingParticipantIds || []).map(id => String(id))
        );
        if (currentUserId) {
            existingSet.add(String(currentUserId));
        }

        const fetchCandidates = async () => {
            if (!cancelled) {
                setLoading(true);
                setFetchError(null);
                setSelectedIds([]);
            }
            try {
                let users = [];
                if (teamId) {
                    const response = await teamService.members(teamId);
                    users = response?.data?.members || [];
                } else {
                    const response = await userService.list();
                    users = response?.data?.users || [];
                }
                const filtered = users.filter(u => !existingSet.has(String(u.id)));
                if (!cancelled) {
                    setCandidates(filtered);
                }
            } catch (err) {
                console.error('Failed to fetch candidates:', err);
                if (!cancelled) {
                    setFetchError('Failed to load users.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchCandidates();

        return () => { cancelled = true; };
    }, [meetingId, teamId, (existingParticipantIds || []).join(',')]);

    const filteredCandidates = useMemo(() => {
        if (!searchQuery.trim()) return candidates;
        const q = searchQuery.toLowerCase();
        return candidates.filter(
            u =>
                u.name?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q)
        );
    }, [candidates, searchQuery]);

    const toggleSelect = (userId) => {
        const id = String(userId);
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = async () => {
        if (selectedIds.length === 0 || submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await videoCallService.addMembers(meeting.id, selectedIds.map(Number));
            notify('success', `${selectedIds.length} member${selectedIds.length > 1 ? 's' : ''} added to the call`);
            const addedUsers = candidates.filter(c => selectedIds.includes(String(c.id)));
            onClose();
            onAdded(addedUsers);
        } catch (err) {
            if (err?.status === 422) {
                const firstError = err?.errors
                    ? Object.values(err.errors).flat()[0]
                    : err?.message;
                setSubmitError(firstError || 'Validation error.');
            } else if (err?.message) {
                setSubmitError(err.message);
            } else {
                setSubmitError('Failed to add members.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const emptyMessage = searchQuery.trim()
        ? 'No users match your search.'
        : candidates.length === 0
            ? 'Everyone has already joined.'
            : 'No users match your search.';

    return (
        <div className="add-members-overlay" onClick={onClose}>
            <div
                className="add-members-container"
                onClick={e => e.stopPropagation()}
            >
                <div className="add-members-header">
                    <span className="add-members-title">Add Members</span>
                    <button
                        className="add-members-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="add-members-search-wrapper">
                    <FaSearch className="add-members-search-icon" />
                    <input
                        className="add-members-search-input"
                        type="text"
                        placeholder="Search by name or email…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                </div>

                <div className="add-members-list">
                    {loading && (
                        <div className="add-members-status-text">Loading…</div>
                    )}
                    {!loading && fetchError && (
                        <div className="add-members-error-text">{fetchError}</div>
                    )}
                    {!loading && !fetchError && filteredCandidates.length === 0 && (
                        <div className="add-members-status-text">{emptyMessage}</div>
                    )}
                    {!loading && !fetchError && filteredCandidates.map(user => {
                        const isSelected = selectedIds.includes(String(user.id));
                        return (
                            <div
                                key={user.id}
                                className={`add-members-row${isSelected ? ' add-members-row--selected' : ''}`}
                                onClick={() => toggleSelect(user.id)}
                            >
                                <div
                                    className={`add-members-avatar add-members-avatar--color-${getAvatarColorIndex(user.name)}`}
                                >
                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="add-members-user-info">
                                    <span className="add-members-user-name">{user.name}</span>
                                    {user.email && (
                                        <span className="add-members-user-email">{user.email}</span>
                                    )}
                                </div>
                                {isSelected && (
                                    <div className="add-members-check">
                                        <FaCheck />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {submitError && (
                    <div className="add-members-footer-error">{submitError}</div>
                )}

                <div className="add-members-footer">
                    <button
                        className="add-members-btn-cancel"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        className="add-members-btn-add"
                        onClick={handleSubmit}
                        disabled={selectedIds.length === 0 || submitting}
                    >
                        {submitting
                            ? 'Adding…'
                            : `Add${selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddMembersModal;
